<template>
  <article
    id="nav-banner"
    class="intro nav-banner row"
    role="region"
  >
    <high-volume-warning-banner v-if="isHighVolumeSplitEnabled" />
    <hgroup
      class="nav-banner-text-container"
      role="presentation"
    >
      <h1
        id="nav-banner-h1"
        class="title-text"
        role="presentation"
      >
        <v-icon
          v-if="mainTitleIcon.name"
          v-bind="mainTitleIcon.props"
          v-text="mainTitleIcon.name"
        />
        {{ mainTitleText }}
      </h1>
      <h3
        id="nav-banner-h3"
        class="banner-text"
        role="presentation"
        v-html="subTitleText"
      />
    </hgroup>
    <aside
      v-if="routeIsCatalog"
      role="banner"
    >
      <img
        v-if="desktopBannerImageUrl"
        id="desktop-banner-ad"
        :src="desktopBannerImageUrl"
        class="desktop-banner-ad"
        :alt="$t('desktop-banner-ad')"
      >
      <img
        v-if="mobileBannerImageUrl"
        id="mobile-banner-ad"
        :src="mobileBannerImageUrl"
        class="mobile-banner-ad"
        :alt="$t('mobile-banner-ad')"
      >
    </aside>
  </article>
</template>

<script>
import { mapGetters } from 'vuex'
import { GET_REWARD_CUSTOMIZATION } from '@/store/get-types'
import { AdCampaignImageType } from '@/cowboy/EnumTypes'
import { AdCampaignMixin } from './shared/ad-campaign/AdCampaignMixin'
import { CATALOG_ROUTE } from '@/router/routes/route-names'
import { TextWithIcon } from '@/components/shared/typography/typography-classes'
import { RL_HIGH_VOLUME_WARNING } from '@/plugins/split/ActiveSplits'
import HighVolumeWarningBanner from '@/components/notifications/HighVolumeWarningBanner'

export default {
  name: 'NavBanner',
  components: { HighVolumeWarningBanner },
  mixins: [AdCampaignMixin],
  computed: {
    ...mapGetters({
      rewardCustomization: GET_REWARD_CUSTOMIZATION
    }),
    isHighVolumeSplitEnabled () {
      return this.$isSplitEnabled(RL_HIGH_VOLUME_WARNING)
    },
    rewardLinkCustomizationDetails () {
      const { rewardCustomization } = this
      const { details } = rewardCustomization || {}
      return details || {}
    },
    mainTitle () {
      const { titleText } = this.rewardLinkCustomizationDetails
      const mainTitle = titleText || this.$route.meta.h1
      return new TextWithIcon(mainTitle)
    },
    mainTitleIcon () {
      return this.mainTitle.icon
    },
    mainTitleText () {
      return this.mainTitle.text
    },
    subTitle () {
      const { bannerText } = this.rewardLinkCustomizationDetails
      const subTitle = bannerText || this.$route.meta.h2
      return new TextWithIcon(subTitle)
    },
    subTitleText () {
      return this.subTitle.text
    },
    routeIsCatalog () {
      return this.$route.name === CATALOG_ROUTE
    },
    desktopBannerImageUrl () {
      return this.getBannerImageUrl(AdCampaignImageType.DESKTOP)
    },
    mobileBannerImageUrl () {
      return this.getBannerImageUrl(AdCampaignImageType.MOBILE)
    }
  },
  methods: {
    getBannerImageUrl (type) {
      let bannerImageUrl = null
      if (this.adCampaignExists(this.rewardCustomization)) {
        const { adCampaign } = this.rewardCustomization
        if (this.adCampaignIsValid(adCampaign)) {
          bannerImageUrl = this.getRandomBannerImageURLFromAdCampaign({
            adCampaign,
            type
          })
        }
      }
      return bannerImageUrl
    },
    getRandomBannerImageURLFromAdCampaign ({ adCampaign, type }) {
      let imageURL = null
      const imageURLs = this.mapAdCampaignImagesToURLs(
        adCampaign.adCampaignImages,
        type
      )
      if (imageURLs.length) {
        imageURL = imageURLs[this.generateRandomIndexForImageURLs(imageURLs)]
      }
      return imageURL
    },
    generateRandomIndexForImageURLs (imageURLs) {
      return Math.floor(Math.random() * imageURLs.length)
    },
    mapAdCampaignImagesToURLs (adCampaignImages, imageType) {
      return adCampaignImages
        .filter(image => image.imageType === imageType)
        .map(image => image.imageUrl)
    },
    adCampaignExists (rewardCustomization) {
      return rewardCustomization && rewardCustomization.adCampaign
    },
    adCampaignIsValid (adCampaign) {
      return (
        this.$_adCampaignMixin_adCampaignIsActive(adCampaign) &&
        this.adCampaignImagesExist(adCampaign)
      )
    },
    adCampaignImagesExist ({ adCampaignImages }) {
      return Array.isArray(adCampaignImages) && adCampaignImages.length
    }
  }
}
</script>

<style scoped lang="scss">
@import '../assets/css/variables';

.nav-banner {
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  margin-top: 128px;
  text-align: center;

  .nav-banner-text-container {
    padding: 30px 20px 15px 20px;
  }
  .title-text {
    font-size: 24px;
    line-height: 29px;
    margin: 0;
    padding: 0;
  }
  .banner-text {
    margin-top: 12px;
  }

  &.intro {
    min-height: 25px;
  }
}
@media screen and (min-width: $breakpoint-md) and (max-width: $breakpoint-lg - 1) {
  .nav-banner {
    margin-top: 100px;
  }
}
@media screen and (max-width: $breakpoint-md - 1) {
  .nav-banner {
    margin-top: 108px;
  }
  .desktop-banner-ad {
    display: none;
  }
  .mobile-banner-ad {
    display: block;
    padding-bottom: 15px;
  }
  .intro {
    min-height: 25px;
    padding: 0;
  }
}
@media screen and (min-width: $breakpoint-md) {
  .desktop-banner-ad {
    display: block;
    padding-bottom: 15px;
  }
  .mobile-banner-ad {
    display: none;
  }
}
</style>
